import PageHeader from "components/PageHeader/PageHeader";
import SafeImage from "components/SafeImage/SafeImage";
import { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getBlogs } from "data/api";
import __posts from "data/jsons/__posts.json";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  NewspaperIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

interface BlogItem {
  id: number | string;
  title: string;
  content?: string;
  author?: { displayName?: string };
  date?: string;
  href: string;
}

const CARD_GRADIENTS = [
  "from-rose-600 via-red-600 to-orange-500",
  "from-blue-700 via-indigo-600 to-violet-600",
  "from-emerald-600 via-teal-600 to-cyan-500",
  "from-amber-500 via-orange-500 to-red-500",
  "from-slate-700 via-blue-800 to-indigo-900",
  "from-fuchsia-600 via-purple-600 to-indigo-600",
];

const getPostImage = (index: number) =>
  (__posts as { featuredImage?: string }[])[index % __posts.length]
    ?.featuredImage;

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const formatDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const BlogCard: FC<{ post: BlogItem; index: number }> = ({ post, index }) => {
  const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
  const image = getPostImage(index);
  const excerpt = post.content
    ? stripHtml(post.content).slice(0, 120) +
      (stripHtml(post.content).length > 120 ? "…" : "")
    : "";

  return (
    <article className="group relative flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-700/80 shadow-bcr-card hover:shadow-bcr-card-hover transition-all duration-300 hover:-translate-y-1 h-full">
      <div
        className={`relative h-28 sm:h-32 bg-gradient-to-br ${gradient} shrink-0`}
      >
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_20%,white,transparent_55%)]" />
        <div className="absolute bottom-3 left-4 right-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur-sm border border-white/25">
            <NewspaperIcon className="w-3 h-3" />
            Article
          </span>
        </div>
      </div>

      <div className="relative flex justify-center -mt-16 px-5">
        <Link
          to={post.href}
          className="block w-full max-w-[220px] aspect-[16/10] rounded-2xl overflow-hidden border-4 border-white dark:border-neutral-900 shadow-xl group-hover:scale-[1.02] transition-transform duration-300"
        >
          <SafeImage
            src={image}
            alt={post.title}
            objectFit="cover"
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        </Link>
      </div>

      <div className="flex flex-col flex-1 px-5 sm:px-6 pt-4 pb-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
          {post.author?.displayName && (
            <span className="inline-flex items-center gap-1">
              <UserCircleIcon className="w-3.5 h-3.5" />
              {post.author.displayName}
            </span>
          )}
          {post.date && (
            <span className="inline-flex items-center gap-1">
              <CalendarDaysIcon className="w-3.5 h-3.5" />
              {formatDate(post.date)}
            </span>
          )}
        </div>

        <h3 className="mt-3 text-base sm:text-lg font-bold text-neutral-900 dark:text-white leading-snug line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          <Link to={post.href} title={post.title}>
            {post.title}
          </Link>
        </h3>

        {excerpt && (
          <p className="mt-2 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-3 flex-1">
            {excerpt}
          </p>
        )}

        <div className="mt-5 pt-4 border-t border-neutral-100 dark:border-neutral-800">
          <Link
            to={post.href}
            className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-[#0f1b3d] hover:bg-[#0a1628] text-white transition-colors"
          >
            Read article
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const BlogPage: FC = () => {
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    getBlogs()
      .then((items) => {
        if (Array.isArray(items)) setBlogs(items);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return blogs;
    return blogs.filter(
      (b) =>
        b.title.toLowerCase().includes(q) ||
        (b.author?.displayName || "").toLowerCase().includes(q) ||
        stripHtml(b.content || "").toLowerCase().includes(q)
    );
  }, [blogs, query]);

  return (
    <div className="nc-BlogPage relative overflow-hidden">
      <Helmet>
        <title>Blog | BCR LTD. UK</title>
        <meta
          name="description"
          content="Read expert articles from BCR Ltd on expansion joint covers, tile trims, LED profiles, building chemicals, and construction innovations across the UAE and GCC."
        />
      </Helmet>

      <PageHeader
        title="Blog"
        subtitle="Expert articles on construction innovations, product guides, and industry trends from BCR LTD."
      />

      <div className="container relative px-4 sm:px-6 py-10 sm:py-12 pb-16 sm:pb-24">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-10">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {!loading &&
              (filtered.length > 0
                ? `${filtered.length} article${filtered.length === 1 ? "" : "s"}`
                : "No articles match your search")}
          </p>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles..."
              className="w-full h-11 pl-11 pr-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none transition-all"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              >
                <div className="h-28 bg-neutral-200 dark:bg-neutral-700" />
                <div className="px-6 pb-6">
                  <div className="w-full max-w-[220px] mx-auto -mt-16 aspect-[16/10] rounded-2xl bg-neutral-300 dark:bg-neutral-600" />
                  <div className="mt-6 h-4 w-1/3 rounded bg-neutral-200 dark:bg-neutral-700" />
                  <div className="mt-3 h-5 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                  <div className="mt-4 h-10 w-full rounded-xl bg-neutral-200 dark:bg-neutral-700" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40">
            <NewspaperIcon className="w-12 h-12 mx-auto text-neutral-400" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
              No articles found
            </h3>
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              Try a different search term.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filtered.map((post, index) => (
              <BlogCard key={post.id} post={post} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
